import { Injectable } from '@angular/core';
import { Selector, State } from '@ngxs/store';
import { AccessState, AccessStateModel, ProfileState, ProfileStateModel, UserModel } from 'shared';
import { PermissionEnum } from 'symbols';
import { ProfileAccess } from './profile-access.model';

@State({ name: 'profileAccess' })
@Injectable()
export class ProfileAccessState {

  /**
   * Generate configs for classroom module depends on provided user role.
   */
  static generateProfileAccess(roles: PermissionEnum[], user: UserModel): ProfileAccess {
    // Return permissions as for unknown role if user not verified or not authenticated.
    if (!user) {
      return this.generateRoleConfig(PermissionEnum.UNKNOWN);
    }

    // Apply permissions of each provided role.
    const rolesConfig: ProfileAccess[] = roles.map(role => this.generateRoleConfig(role));

    // Return defined config.
    return this.mergeConfig(rolesConfig);
  }

  /**
   * Define and create config using provided config data.
   */
  static generateRoleConfig(role: PermissionEnum): ProfileAccess {
    switch (role) {
      case PermissionEnum.ADMIN:
        return {
          canChangeAvatar: true,
          canChangePhone: true,
          canChangeEmail: true
        };

      case PermissionEnum.SUPER_USER:
        return {
          canChangeAvatar: true,
          canChangePhone: true,
          canChangeEmail: true
        };
      default: {
        return {
          canChangeAvatar: false,
          canChangeEmail: false,
          canChangePhone: false
        };
      }
    }
  }

  /**
   * Merge provided roles configs (by union rule).
   */
  static mergeConfig(rolesConfig: ProfileAccess[]): ProfileAccess {
    const moduleConfig: ProfileAccess = {
      canChangeAvatar: false,
      canChangeEmail: false,
      canChangePhone: false
    };

    for (const config of rolesConfig) {
      moduleConfig.canChangeAvatar = moduleConfig.canChangeAvatar ? true : config.canChangeAvatar;
      moduleConfig.canChangeEmail = moduleConfig.canChangeEmail ? true : config.canChangeEmail;
      moduleConfig.canChangePhone = moduleConfig.canChangePhone ? true : config.canChangePhone;
    }

    return moduleConfig;
  }

  /**
   * Define and create module access rules using provided portal data.
   */
  @Selector([ProfileState, AccessState])
  static accessToProfile(profileState: ProfileStateModel, accessState: AccessStateModel): ProfileAccess {
    const userData = profileState.currentUser;
    const roleList = accessState.roles;

    return ProfileAccessState.generateProfileAccess(roleList, userData);
  }

}
